import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { UnifiedChatbotData, UnifiedChatbotSettings, DEFAULT_UNIFIED_SETTINGS } from '@/types/builder';
import { ChatTheme, Question, TriggerRule } from '@/types/theme';
import { Json } from '@/integrations/supabase/types';
import { transformThemeData, prepareThemeForDatabase } from '@/utils/themeUtils';

// Validation function for UUID
const isValidUUID = (uuid: string) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

// Transform legacy settings to unified format
const transformLegacySettings = (
  settings: any,
  aiSettings: any,
  chatbot: any
): UnifiedChatbotSettings => {
  const unified = { ...DEFAULT_UNIFIED_SETTINGS };

  // Merge existing settings
  if (settings) {
    console.log('Transform: settings.chatMode =', settings.chatMode);
    console.log('Transform: unified.chatMode before =', unified.chatMode);
    unified.chatMode = settings.chatMode || unified.chatMode;
    console.log('Transform: unified.chatMode after =', unified.chatMode);
    unified.messageDelay = settings.messageDelay ?? unified.messageDelay;
    unified.buttonOptionsDelay = settings.buttonOptionsDelay ?? unified.buttonOptionsDelay;
    unified.noResponseDelay = settings.noResponseDelay ?? unified.noResponseDelay;
    unified.optionsHandlerDelay = settings.optionsHandlerDelay ?? unified.optionsHandlerDelay;
    unified.showTypingIndicator = settings.showTypingIndicator ?? unified.showTypingIndicator;
    unified.showTimestamp = settings.showTimestamp ?? unified.showTimestamp;
    unified.validation_settings = settings.validation_settings || unified.validation_settings;
    unified.companyName = settings.companyName || unified.companyName;
    unified.companyLogo = settings.companyLogo || unified.companyLogo;
    unified.tcpaLanguage = settings.tcpaLanguage || unified.tcpaLanguage;
  }

  // Merge AI settings
  if (aiSettings) {
    if (aiSettings.ai_chat_config) {
      unified.ai_chat_config = { ...unified.ai_chat_config, ...aiSettings.ai_chat_config };
    }
    if (aiSettings.provider) {
      unified.ai_provider = { ...unified.ai_provider, ...aiSettings.provider };
    }
  }

  // Merge chatbot fields
  if (chatbot) {
    unified.custom_javascript = chatbot.custom_javascript || unified.custom_javascript;
    unified.custom_html = chatbot.custom_html || unified.custom_html;
    unified.operatorName = chatbot.operator_name || unified.operatorName;
    unified.operatorAvatarUrl = chatbot.operator_avatar_url || unified.operatorAvatarUrl;
    unified.companyName = chatbot.company_name || unified.companyName;
    unified.companyLogo = chatbot.company_logo || unified.companyLogo;
  }

  return unified;
};

// Transform questions from database format
const transformQuestions = (questions: any[]): Question[] => {
  return questions.map((q: any) => {
    let parsedOptions: { label: string; value: string }[] = [];
    if (q.options) {
      try {
        parsedOptions = typeof q.options === 'string' ? JSON.parse(q.options) : q.options;
        if (!Array.isArray(parsedOptions)) {
          parsedOptions = [];
        }
      } catch (e) {
        console.error('Error parsing options:', e);
        parsedOptions = [];
      }
    }

    let validationRules: any = {};
    let validationType = 'none';

    if (q.responsevalidation) {
      try {
        const validationSettings = typeof q.responsevalidation === 'string' 
          ? JSON.parse(q.responsevalidation) 
          : q.responsevalidation;

        if (validationSettings.validationType) {
          validationType = validationSettings.validationType;
        }

        if (validationSettings.validation_rules) {
          validationRules = {
            ...validationRules,
            ...validationSettings.validation_rules
          };
        }
      } catch (e) {
        console.error('Error parsing responsevalidation:', e);
      }
    }

    let richContent = null;
    if (q.rich_content) {
      try {
        richContent = typeof q.rich_content === 'string' ? JSON.parse(q.rich_content) : q.rich_content;
      } catch (e) {
        console.error('Error parsing rich_content:', e);
        richContent = null;
      }
    }

    return {
      id: q.id,
      chatbot_id: q.chatbot_id,
      text: q.text,
      type: q.type as 'text' | 'number' | 'select' | 'boolean' | 'button' | 'no_response',
      options: parsedOptions,
      order: q.order,
      shortname: q.shortname,
      errormessage: q.errormessage,
      responsevalidation: q.responsevalidation,
      validation_rules: validationRules,
      validationType: q.validationType || validationType || 'none',
      created_at: q.created_at,
      updated_at: q.updated_at,
      placeholder: q.placeholder,
      rich_content: richContent
    };
  });
};

// Transform trigger rules from database format
const transformTriggerRules = (triggerRules: any[]): TriggerRule[] => {
  return triggerRules.map((rule: any) => {
    let conditions: any[] = [];
    if (rule.conditions) {
      try {
        conditions = typeof rule.conditions === 'string' ? JSON.parse(rule.conditions) : rule.conditions;
      } catch (e) {
        console.error('Error parsing conditions:', e);
        conditions = [];
      }
    }

    let postVariableMappings: any[] = [];
    if (rule.postvariablemappings) {
      try {
        postVariableMappings = typeof rule.postvariablemappings === 'string' 
          ? JSON.parse(rule.postvariablemappings) 
          : rule.postvariablemappings;
      } catch (e) {
        console.error('Error parsing postvariablemappings:', e);
        postVariableMappings = [];
      }
    }

    return {
      id: rule.id,
      chatbot_id: rule.chatbot_id,
      name: rule.name,
      notes: rule.notes || '',
      conditions: conditions.map((condition: any) => ({
        questionId: condition.questionId || '',
        operator: condition.operator || 'equals',
        value: typeof condition.value === 'string' ? condition.value : String(condition.value ?? '')
      })),
      webhookUrl: rule.webhookurl || '',
      postVariableMappings: postVariableMappings.map((mapping: any) => ({
        questionId: mapping.questionId || '',
        variableName: mapping.variableName || '',
        providerId: mapping.providerId
      })),
      active: rule.active !== false,
      created_at: rule.created_at,
      updated_at: rule.updated_at
    };
  });
};

// Main hook for unified chatbot data management
export const useChatbotBuilder = (chatbotId: string | undefined) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  // Validate chatbot ID
  const validChatbotId = chatbotId && isValidUUID(chatbotId) ? chatbotId : undefined;

  console.log('useChatbotBuilder:', {
    inputChatbotId: chatbotId,
    validChatbotId,
    isValid: chatbotId ? isValidUUID(chatbotId) : false
  });

  // Single unified query for all chatbot data
  const {
    data: rawData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['chatbot-builder', validChatbotId],
    queryFn: async () => {
      if (!validChatbotId) {
        throw new Error('Invalid chatbot ID format');
      }

      // Fetch all data in parallel
      const [
        { data: chatbot, error: chatbotError },
        { data: theme, error: themeError },
        { data: questions, error: questionsError },
        { data: triggerRules, error: triggerRulesError }
      ] = await Promise.all([
        supabase.from('chatbots').select('*').eq('id', validChatbotId).single(),
        supabase.from('themes').select('*').eq('chatbot_id', validChatbotId).single(),
        supabase.from('questions').select('*').eq('chatbot_id', validChatbotId).order('order', { ascending: true }),
        supabase.from('trigger_rules').select('*').eq('chatbot_id', validChatbotId).order('created_at', { ascending: true })
      ]);

      // Handle errors
      if (chatbotError) {
        console.error('Chatbot error:', chatbotError);
        if (chatbotError.code === 'PGRST116') {
          throw new Error('Chatbot not found');
        }
        throw new Error(`Failed to load chatbot: ${chatbotError.message}`);
      }

      console.log('Loaded chatbot data:', {
        chatbot: chatbot ? { id: chatbot.id, name: chatbot.name } : null,
        theme: theme ? { id: theme.id, name: theme.name } : null,
        questionsCount: questions?.length || 0,
        triggerRulesCount: triggerRules?.length || 0
      });

      if (themeError && themeError.code !== 'PGRST116') {
        console.warn('Theme not found, using default');
      }

      if (questionsError) {
        throw new Error(`Failed to load questions: ${questionsError.message}`);
      }

      if (triggerRulesError) {
        throw new Error(`Failed to load trigger rules: ${triggerRulesError.message}`);
      }

      // Transform and combine data
      console.log('Raw chatbot.settings from DB:', chatbot.settings);
      console.log('Type of chatbot.settings:', typeof chatbot.settings);

      // Parse settings if it's a string
      let parsedSettings = chatbot.settings;
      if (typeof chatbot.settings === 'string') {
        try {
          parsedSettings = JSON.parse(chatbot.settings);
          console.log('Parsed settings:', parsedSettings);
        } catch (e) {
          console.error('Failed to parse settings JSON:', e);
          parsedSettings = {};
        }
      }

      console.log('=== AI SETTINGS DEBUG ===');
      console.log('parsedSettings.ai_chat_config:', parsedSettings?.ai_chat_config);
      console.log('parsedSettings.ai_chat_config.aiPersonality:', parsedSettings?.ai_chat_config?.aiPersonality);
      console.log('AI name from DB:', parsedSettings?.ai_chat_config?.aiPersonality?.name);
      console.log('=== END AI SETTINGS DEBUG ===');

      const unifiedSettings = transformLegacySettings(
        parsedSettings,
        parsedSettings, // AI settings are stored in the same settings object
        chatbot
      );
      console.log('Transformed unified settings:', unifiedSettings);

      // Transform theme data if it exists
      let transformedTheme: ChatTheme | null = null;
      if (theme) {
        // Transform theme data to include display fields with defaults
        transformedTheme = transformThemeData(theme);
        console.log('Loaded theme:', transformedTheme);
      } else {
        console.log('No theme found for chatbot:', validChatbotId);
      }

      const unifiedData: UnifiedChatbotData = {
        id: chatbot.id,
        user_id: chatbot.user_id,
        name: chatbot.name,
        description: chatbot.description,
        title: chatbot.title,
        avatar_url: chatbot.avatar_url,
        welcome_message: chatbot.welcome_message,
        completion_message: chatbot.completion_message,
        active: chatbot.active,
        created_at: chatbot.created_at,
        updated_at: chatbot.updated_at,
        settings: unifiedSettings,
        theme: transformedTheme,
        questions: transformQuestions(questions || []),
        triggerRules: transformTriggerRules(triggerRules || [])
      };

      return unifiedData;
    },
    enabled: !!validChatbotId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      if (error.message.includes('not found')) {
        return false;
      }
      return failureCount < 2;
    }
  });

  // Hook is now used inside BuilderProvider, so no need to dispatch to context

  // Debounced save mutation with settings merging
  const saveMutation = useMutation({
    mutationFn: async ({
      chatbotId,
      updates
    }: {
      chatbotId: string;
      updates: Partial<UnifiedChatbotData>
    }) => {
      console.log('Saving chatbot updates:', { chatbotId, updates });
      console.log('Updates.settings:', updates.settings);
      console.log('Updates.settings.ai_chat_config:', updates.settings?.ai_chat_config);

      const updateData: any = { ...updates };

      // If we're updating settings, merge with current database settings to prevent overwrites
      if (updates.settings) {
        console.log('🔄 Merging settings with database to prevent overwrites...');
        console.log('🔄 ChatMode in updates.settings:', updates.settings.chatMode);

        // Get current settings from database
        const { data: currentChatbot, error: fetchError } = await supabase
          .from('chatbots')
          .select('settings')
          .eq('id', chatbotId)
          .single();

        if (fetchError) {
          console.error('❌ Error fetching current settings:', fetchError);
          throw fetchError;
        }

        console.log('🔄 Raw settings from database:', currentChatbot?.settings);
        console.log('🔄 Type of settings from database:', typeof currentChatbot?.settings);

        // Parse settings if they're stored as JSON string
        let currentSettings = {};
        if (currentChatbot?.settings) {
          if (typeof currentChatbot.settings === 'string') {
            try {
              currentSettings = JSON.parse(currentChatbot.settings);
              console.log('🔄 Parsed settings from JSON string:', currentSettings);
            } catch (e) {
              console.error('❌ Error parsing settings JSON:', e);
              currentSettings = {};
            }
          } else {
            currentSettings = currentChatbot.settings;
            console.log('🔄 Settings already object:', currentSettings);
          }
        }

        console.log('🔄 Current chatMode in database:', currentSettings.chatMode);
        console.log('🔄 New chatMode from updates:', updates.settings.chatMode);

        const mergedSettings = {
          ...currentSettings,
          ...updates.settings
        };

        console.log('🔄 Debounced save - Settings merge:', {
          current: currentSettings,
          updates: updates.settings,
          merged: mergedSettings,
          hasChatMode: !!mergedSettings.chatMode,
          chatModeValue: mergedSettings.chatMode,
          messageDelay: mergedSettings.messageDelay,
          messageDelayType: typeof mergedSettings.messageDelay
        });

        updateData.settings = JSON.stringify(mergedSettings);
        console.log('🔄 Debounced save - Final serialized settings:', updateData.settings);
        console.log('🔄 ChatMode in final serialized:', JSON.parse(updateData.settings).chatMode);
      }

      // Don't clear ai_settings - preserve existing AI configuration
      // Remove ai_settings from updates to avoid overwriting
      delete updateData.ai_settings;

      updateData.updated_at = new Date().toISOString();

      console.log('Final updateData being sent to DB:', updateData);

      const { data, error } = await supabase
        .from('chatbots')
        .update(updateData)
        .eq('id', chatbotId)
        .select()
        .single();

      if (error) throw error;

      console.log('✅ Database response after save:', data);
      console.log('✅ Saved settings from DB (raw):', data.settings);
      console.log('✅ Type of saved settings:', typeof data.settings);
      
      // Parse and verify the saved settings
      if (data.settings) {
        try {
          const parsedSettings = typeof data.settings === 'string' ? JSON.parse(data.settings) : data.settings;
          console.log('✅ Parsed saved settings:', parsedSettings);
          console.log('✅ ChatMode in saved settings:', parsedSettings.chatMode);
        } catch (e) {
          console.error('❌ Error parsing saved settings:', e);
        }
      }

      return data;
    },
    onSuccess: () => {
      toast({
        title: 'Settings saved',
        description: 'Your changes have been saved successfully.',
      });
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['chatbot-builder', validChatbotId] });
    },
    onError: (error) => {
      console.error('Save error:', error);
      toast({
        title: 'Error saving changes',
        description: error.message || 'Please try again',
        variant: 'destructive'
      });
    }
  });

  // Theme save mutation
  const saveThemeMutation = useMutation({
    mutationFn: async ({ 
      chatbotId, 
      theme 
    }: { 
      chatbotId: string; 
      theme: ChatTheme 
    }) => {
      const dbTheme = prepareThemeForDatabase(theme);
      const { data, error } = await supabase
        .from('themes')
        .upsert({
          chatbot_id: chatbotId,
          ...dbTheme,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: 'Theme saved',
        description: 'Your theme has been saved successfully.',
      });
      
      queryClient.invalidateQueries({ queryKey: ['chatbot-builder', validChatbotId] });
    },
    onError: (error) => {
      console.error('Theme save error:', error);
      toast({
        title: 'Error saving theme',
        description: error.message || 'Please try again',
        variant: 'destructive'
      });
    }
  });

  // Trigger rules save mutation
  const saveTriggerRulesMutation = useMutation({
    mutationFn: async ({
      chatbotId,
      triggerRules
    }: {
      chatbotId: string;
      triggerRules: TriggerRule[]
    }) => {
      console.log('🎯 Saving trigger rules:', triggerRules);

      // Delete existing rules for this chatbot
      const { error: deleteError } = await supabase
        .from('trigger_rules')
        .delete()
        .eq('chatbot_id', chatbotId);

      if (deleteError) {
        console.error('❌ Error deleting existing trigger rules:', deleteError);
        throw deleteError;
      }

      // Insert all rules (now using proper UUIDs)
      const rulesToSave = triggerRules.map(rule => ({
          id: rule.id,
          chatbot_id: chatbotId,
          name: rule.name,
          conditions: JSON.stringify(rule.conditions || []),
          postvariablemappings: JSON.stringify(rule.postVariableMappings || []),
          active: rule.active,
          notes: rule.notes || '',
          webhookurl: rule.webhookUrl || '',
          created_at: rule.created_at,
          updated_at: new Date().toISOString()
        }));

      if (rulesToSave.length > 0) {
        const { data, error } = await supabase
          .from('trigger_rules')
          .insert(rulesToSave)
          .select();

        if (error) {
          console.error('❌ Error inserting trigger rules:', error);
          throw error;
        }

        console.log('✅ Trigger rules saved successfully:', data);
        return data;
      }

      return [];
    },
    onSuccess: () => {
      toast({
        title: 'Trigger rules saved',
        description: 'Your trigger rules have been saved successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['chatbot-builder', validChatbotId] });
    },
    onError: (error) => {
      console.error('❌ Trigger rules save error:', error);
      toast({
        title: 'Error saving trigger rules',
        description: error.message || 'Please try again',
        variant: 'destructive'
      });
    }
  });

  // Debounced save function with chatMode conflict prevention
  const debouncedSave = useCallback((updates: Partial<UnifiedChatbotData>) => {
    if (!validChatbotId) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout
    saveTimeoutRef.current = setTimeout(() => {
      // Before saving, check if there was a recent immediate chatMode save
      // If so, merge the chatMode from the database to prevent overwriting
      saveMutation.mutate({ chatbotId: validChatbotId, updates });
    }, 1000);
  }, [validChatbotId, saveMutation]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return {
    // Data
    data: rawData,
    isLoading,
    error: error?.message || null,
    isSaving: saveMutation.isPending,

    // Actions
    refetch,
    debouncedSave,
    saveTheme: (theme: ChatTheme) => saveThemeMutation.mutate({ chatbotId: validChatbotId!, theme }),
    saveTriggerRules: (triggerRules: TriggerRule[]) => saveTriggerRulesMutation.mutate({ chatbotId: validChatbotId!, triggerRules }),

    // Mutations
    saveMutation,
    saveThemeMutation,
    saveTriggerRulesMutation
  };
};
